import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Trash2, Loader2 } from "lucide-react";
import { apiPost, apiGet, formatLKR, formatPct } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
  { value: "target_reached", label: "Reached target price" },
  { value: "signal_deteriorated", label: "Signal deteriorated" },
  { value: "better_opportunity", label: "Better opportunity found" },
  { value: "rebalancing", label: "Portfolio rebalancing" },
  { value: "need_cash", label: "Need cash / liquidity" },
  { value: "other", label: "Other" },
];

/**
 * SellHoldingDialog — Phase 7 Stage 8 + Stage 1 (brokerage).
 *
 * Captures sale price + reason and posts to
 *   POST /api/holdings/{id}/sell
 * which computes selling brokerage, net proceeds, and brokerage-aware
 * realised P&L using `total_cost_inc_charges`.
 */
const SellHoldingDialog = ({ open, onClose, onExited, holding }) => {
  const [salePrice, setSalePrice] = useState("");
  const [saleDate, setSaleDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState(REASONS[0].value);
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rates, setRates] = useState({ buy_rate: 0.0121, sell_rate: 0.0121 });

  useEffect(() => {
    if (open) {
      apiGet("/portfolio-settings").then(setRates).catch(() => {});
      // Default sale price to current market price for convenience
      if (holding?.current_price) {
        setSalePrice(String(holding.current_price));
      } else {
        setSalePrice("");
      }
      setSaleDate(new Date().toISOString().slice(0, 10));
      setReason(REASONS[0].value);
      setOther("");
      setPreview(null);
    }
  }, [open, holding]);

  const shares = holding?.shares_purchased || 0;
  const costInc = holding?.total_cost_inc_charges || holding?.cost_basis || 0;

  // Live preview
  useEffect(() => {
    const p = Number(salePrice);
    if (!open || !shares || !p || p <= 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    apiPost("/portfolio-settings/preview-sell", {
      shares,
      sale_price: p,
      original_cost_inc_brokerage: costInc,
    })
      .then((r) => { if (!cancelled) setPreview(r); })
      .catch(() => {
        // local fallback
        const gross = shares * p;
        const brokerage = gross * rates.sell_rate;
        const net = gross - brokerage;
        const realised = net - costInc;
        if (!cancelled) {
          setPreview({
            gross_proceeds: gross,
            brokerage_sell: brokerage,
            net_proceeds: net,
            realised_pl_lkr: realised,
            realised_pl_pct: costInc > 0 ? (realised / costInc) * 100 : 0,
          });
        }
      });
    return () => { cancelled = true; };
  }, [salePrice, open, shares, costInc, rates.sell_rate]);

  const submit = async () => {
    const p = Number(salePrice);
    if (!p || p <= 0) {
      toast.error("Enter a valid sale price");
      return;
    }
    setBusy(true);
    try {
      await apiPost(`/holdings/${holding.holding_id}/sell`, {
        sale_price: p,
        sale_date: saleDate,
        reason,
        reason_other: reason === "other" ? other.trim() : null,
      });
      toast.success(`Closed position in ${holding.cse_symbol}`, {
        description: preview
          ? `Net proceeds: ${formatLKR(preview.net_proceeds)} · Realised P&L: ${preview.realised_pl_lkr >= 0 ? "+" : ""}${formatLKR(preview.realised_pl_lkr)}`
          : "Saved to your exit history.",
      });
      onExited?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not close position");
    } finally {
      setBusy(false);
    }
  };

  if (!holding) return null;
  const ratePct = (rates.sell_rate * 100).toFixed(2);
  const gainPositive = preview && (preview.realised_pl_lkr ?? 0) >= 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="sm:max-w-lg"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid="sell-holding-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-[hsl(var(--destructive))]" />
            Record sale of {holding.cse_symbol}
          </DialogTitle>
          <DialogDescription>
            {holding.shares_purchased} shares · Original cost (incl. buy brokerage):{" "}
            <span className="font-medium text-foreground tabular">{formatLKR(costInc)}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sale-price">Sale price (LKR / share)</Label>
              <Input
                id="sale-price"
                type="number"
                min="0"
                step="0.01"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                data-testid="sell-price-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sale-date">Sale date</Label>
              <Input
                id="sale-date"
                type="date"
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
                data-testid="sell-date-input"
              />
            </div>
          </div>

          {preview && (
            <div
              data-testid="sell-preview"
              className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-1.5"
            >
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Gross proceeds</span>
                <span className="tabular font-medium">{formatLKR(preview.gross_proceeds)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Selling brokerage ({ratePct}%)</span>
                <span className="tabular font-medium text-[hsl(var(--destructive))]">−{formatLKR(preview.brokerage_sell)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-1.5">
                <span className="font-medium">Net proceeds</span>
                <span className="tabular font-semibold">{formatLKR(preview.net_proceeds)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Original cost (incl. buy brokerage)</span>
                <span className="tabular">−{formatLKR(costInc)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-1.5 text-sm">
                <span className="font-medium">Realised P&amp;L</span>
                <span className={`tabular font-semibold ${gainPositive ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>
                  {gainPositive ? "+" : ""}{formatLKR(preview.realised_pl_lkr)}
                  <span className="ml-1 text-[11px] font-normal">({formatPct(preview.realised_pl_pct)})</span>
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--success))] pt-1 border-t border-border">
                <span>✅</span>
                <span>No capital gains tax applies on profits from CSE listed shares</span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Why are you selling?</Label>
            <RadioGroup
              value={reason}
              onValueChange={setReason}
              className="space-y-2"
              data-testid="sell-reason-radio"
            >
              {REASONS.map((r) => (
                <div key={r.value} className="flex items-center gap-2">
                  <RadioGroupItem
                    value={r.value}
                    id={`reason-${r.value}`}
                    data-testid={`sell-reason-${r.value}`}
                  />
                  <Label
                    htmlFor={`reason-${r.value}`}
                    className="cursor-pointer text-sm font-normal"
                  >
                    {r.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            {reason === "other" && (
              <Input
                value={other}
                onChange={(e) => setOther(e.target.value)}
                placeholder="Tell us briefly…"
                data-testid="sell-reason-other-input"
                className="mt-1"
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !Number(salePrice) || (reason === "other" && !other.trim())}
            className="gap-2"
            data-testid="sell-confirm-button"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Record sale
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SellHoldingDialog;
